<template>
  <div>
    <div v-if="loadedData && loadedData.status === 'failure'" class="alert alert-danger">
      {{ loadedData.message }}
    </div>
    <div v-else-if="uploadStatus === 'success'" class="alert alert-success">
      Vielen Dank! Die Dateien wurden erfolgreich übertragen!
    </div>
    <div v-else class="row">
      <div class="col-lg-12">
        <b-form-group>
          <h2>Dateien Hochladen</h2>
          <p>{{ description }}</p>
          <b-table responsive :fields="columns" :items="selectedFiles" class="mt-4" table-class="fmx-table">
            <template v-slot:cell(description)="row">
              <b-form-input :placeholder="defaultDescription(row.item.name)" :disabled="sending"
                @input="desc => updateFileDescription(row.item.name, desc)">
              </b-form-input>
            </template>
            <template v-slot:cell(size)="row">
              <label>{{ userFriendlyFileSize(row.item.size) }}</label>
            </template>
            <template v-slot:cell(actions)="row">
              <label class="btn btn-outline-primary" :class="{ 'disabled': sending }"
                @click="deleteFile(row.item.name)">X</label>
            </template>
            <template v-slot:custom-foot>
              <b-tr>
                <b-th></b-th>
                <b-th></b-th>
                <b-th>
                  {{ userFriendlyFileSize(selectedFilesSize) }}
                </b-th>
                <b-th></b-th>
              </b-tr>
            </template>
            <template v-slot:table-caption>
              <div v-if="uploadStatus !== 'waiting'">
                <b-alert :show="uploadStatus === 'failure'" dismissible fade variant="danger">
                  <div>
                    Tut uns leid! Beim Übertragen der Dateien ist ein technischer Fehler aufgetreten.
                    Bitte wenden Sie sich an Ihren Kontakt bei fumiX oder den allgemeinen Support
                    unter +49 6221 829949 / support@fumix.de !
                  </div>
                </b-alert>
              </div>
              <div v-else>
                <time-out-alert :visible="alreadySelectedFileNames.length > 0" @dismissed="alreadySelectedFileNames = ''">
                  <template #content>
                    Die Datei <span style="font-weight: bold">{{ alreadySelectedFileNames }}</span>
                    wurde bereits ausgewählt.
                  </template>
                </time-out-alert>
                <time-out-alert :visible="exceedingMaxFileSizeFileNames.length > 0"
                  @dismissed="exceedingMaxFileSizeFileNames = ''">
                  <template #content>
                    Die Datei <span style="font-weight: bold">{{ exceedingMaxFileSizeFileNames }}</span>
                    kann nicht hochgeladen werden, weil sie größer als {{
                      userFriendlyFileSize(fileMaxSize) }} ist.
                  </template>
                </time-out-alert>
              </div>
            </template>
          </b-table>
          <div>
            <label for="file" class="btn btn-outline-primary" :class="{ 'disabled': sending }">Datei
              auswählen</label>
            <input type="file" ref="file" id="file" style="visibility:hidden; opacity:0; z-index:-1; position:absolute"
              multiple="multiple" @change="selectFiles()">
            <label v-if="selectedFiles.length > 0" class="btn btn-outline-primary" :class="{ 'disabled': sending }"
              @click="sendFiles()">
              {{ sendButtonLabel }}
            </label>
          </div>
        </b-form-group>
      </div>
    </div>
  </div>
</template>
<script>
import TimeOutAlert from './TimeOutAlert.vue';

export default {
  components: {
    TimeOutAlert
  },
  props: {
    apiUrl: {
      type: String,
      required: true,
      default: ''
    },
    issueId: {
      type: String,
      required: true
    },
    uploadCode: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      selectedFiles: [],
      alreadySelectedFileNames: '',
      exceedingMaxFileSizeFileNames: '',
      sending: false,
      uploadStatus: 'waiting',
    }
  },
  asyncComputed: {
    loadedData: {
      get() {
        // return this.$http.post(this.apiUrl, {issueId: this.issueId, uploadCode: this.uploadCode}).
        return this.$http.get(this.apiUrl, {
          params: {
            issueId: this.issueId,
            uploadCode: this.uploadCode
          }
        }).then(response => {
          return response.body.data;
        });
      }
    }
  },
  computed: {
    sendButtonLabel() {
      return this.selectedFiles.length > 1 ? "Dateien versenden" : "Datei versenden"
    },
    columns() {
      return [
        { key: 'name', label: 'Dateiname' },
        { key: 'description', label: 'Beschreibung' },
        { key: 'size', label: 'Dateigröße' },
        { key: 'actions', label: 'Entfernen' },
      ]
    },
    description() {
      return `Hier können Sie einfach und sicher Dateien hochladen und diese an das fumiX-Team weiterleiten.
      Bitte beachten Sie, dass die Größe einer Datei ${this.userFriendlyFileSize(this.fileMaxSize)} nicht überschreiten darf.`
    },
    selectedFilesSize() {
      return this.selectedFiles.map(file => file.size).reduce((sum, curSize) => sum = sum + curSize, 0);
    },
    fileMaxSize() {
      return this.loadedData ?
        this.loadedData.fileUploadConstraints.fileMaxSize
        : 0;
    }
  },
  methods: {
    async selectFiles() {
      let files = Array.from(this.$refs.file.files);
      let fileNames = files.map(file => file.name);

      this.alreadySelectedFileNames = this.selectedFiles
        .filter(selFile => fileNames.includes(selFile.name))
        .map(selFile => selFile.name)
        .join(',');

      this.exceedingMaxFileSizeFileNames = files.filter(file => file.size > this.fileMaxSize)
        .map(file => file.name)
        .join(',');

      let validUpload =
        this.alreadySelectedFileNames.length === 0 &&
        this.exceedingMaxFileSizeFileNames.length === 0;

      if (validUpload) {
        files.forEach(file => this.selectedFiles.push(file));
      }
    },
    updateFileDescription(fileName, desc) {
      let file = this.selectedFiles.find(file => file.name === fileName)
      if (file) {
        file.description = desc;
      }
    },
    deleteFile(fileName) {
      this.selectedFiles = this.selectedFiles.filter(file => file.name !== fileName);
    },
    async sendFiles() {
      // The order is significant. issueId and uploadCode have to be transmitted here,
      // it's not working as parameters.
      let formData = new FormData();
      formData.append('issueId', this.issueId);
      formData.append('uploadCode', this.uploadCode);
      this.selectedFiles.forEach((file, i) => {
        formData.append(`description-${i}`, file.description ? file.description : "");
        formData.append(`file-${i}`, file, file.name);
      });

      this.sending = true;
      try {
        let response = await this.$http.post(this.apiUrl + '/support', formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        this.uploadStatus = 'success';
        this.sending = false;
      } catch (error) {
        this.uploadStatus = 'failure';
        this.sending = false;
      }
    },
    defaultDescription(name) {
      if (name) {
        return 'Beschreibung von ' + name;
      }
      return "";
    },
    userFriendlyFileSize(bytes, precision = 0) {
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
      if (!bytes || bytes === 0) {
        return "0";
      }
      let i = 0;
      while (bytes >= 1024) {
        i++;
        bytes = bytes / 1024;
      }
      return bytes.toFixed(precision) + " " + sizes[i];
    },
    /** Listener that gets updated by the alert component on every second */
    updateAlertShowDuration(sec = defaultAlertShowDuration) {
      this.alertShowDuration = sec;
      if (this.alertShowDuration === 0) {
        this.exceedingMaxFileSizeFileNames = '';
        this.exceedingMaxFormSizeFileNames = '';
      }
    }
  }// end methods,
};
</script>
<style>
.fmx-table {
  color: whitesmoke !important;
}

.disabled {
  pointer-events: none;
  border-color: whitesmoke !important;
  color: whitesmoke !important;
}
</style>